import { debtShareToken, erc20 as erc20ABI, pair as pairABI, Swap } from '@jdiyield/abi'
import {
  alpacaDebtTokens,
  BALANCER_LPs,
  // DOLLY,
  // DAI,
  BNB,
  BUSD,
  ChainID,
  // ChainID, // USDT,
  DOP_LPS,
  EPS_3P,
  IRON_LPs,
  mebBNB,
  mebBUSD,
  WEX
} from '@jdiyield/constants'
import { AbiItem, Balance, BalanceLP, BaseBalance, BaseLPBalance, PriceList, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import { getPrice } from '..'
import { weiToEth } from './unit'
import { findToken, makeBatchRequest } from './utils'

// import { web3Contract } from './web3'

// const stablecoins = [USDT, BUSD, DOLLY, DAI]

const pair = pairABI as AbiItem | AbiItem[]
const erc20 = erc20ABI as AbiItem | AbiItem[]

export async function isLP(web3: Web3, lp: BaseBalance): Promise<boolean> {
  const contractLP = new web3.eth.Contract(pair, lp.lpAddress)
  try {
    await contractLP.methods.getReserves().call()
    return true
  } catch (e) {
    return false
  }
}

export async function calculate(
  web3: Web3,
  lp: BaseBalance,
  routerAddress: string,
  tokens: Token[],
  priceList: PriceList,
  chainId?: ChainID
): Promise<Balance | BalanceLP> {
  const _isLP = await isLP(web3, lp)
  if (_isLP) {
    return calculateBalanceLP(web3, lp, routerAddress, tokens, priceList, chainId)
  } else {
    return calculateBalanceSingle(web3, lp, tokens, priceList)
  }
}

export async function calculateBalanceLP(
  web3: Web3,
  lp: BaseBalance,
  _routerContractAddress: string,
  tokens: Token[],
  priceList?: PriceList,
  chainId?: ChainID
): Promise<BalanceLP> {
  try {
    const contractLP = new web3.eth.Contract(pair, lp.lpAddress)
    const [totalSupply, { '0': reserveA, '1': reserveB }, tokenA, tokenB, lpDecimals] = await makeBatchRequest(web3, [
      contractLP.methods.totalSupply().call,
      contractLP.methods.getReserves().call,
      contractLP.methods.token0().call,
      contractLP.methods.token1().call,
      contractLP.methods.decimals().call
    ])

    const tokenAInfo = findToken(tokens, tokenA)
    let tokenASymbol = tokenAInfo?.symbol
    let tokenADecimals = tokenAInfo?.decimals
    if (!tokenAInfo) {
      const tokenAContract = new web3.eth.Contract(erc20, tokenA)
      const [symbol, decimals] = await makeBatchRequest(web3, [
        tokenAContract.methods.symbol().call,
        tokenAContract.methods.decimals().call
      ])
      tokenASymbol = symbol
      tokenADecimals = decimals
    }

    const tokenBInfo = findToken(tokens, tokenB)
    let tokenBSymbol = tokenBInfo?.symbol
    let tokenBDecimals = tokenBInfo?.decimals
    if (!tokenBInfo) {
      const tokenBContract = new web3.eth.Contract(erc20, tokenB)
      const [symbol, decimals] = await makeBatchRequest(web3, [
        tokenBContract.methods.symbol().call,
        tokenBContract.methods.decimals().call
      ])
      tokenBSymbol = symbol
      tokenBDecimals = decimals
    }

    const tokenAmountA = new BigNumber(lp.balance).dividedBy(totalSupply).multipliedBy(reserveA)
    const tokenAmountB = new BigNumber(lp.balance).dividedBy(totalSupply).multipliedBy(reserveB)

    const tokenABalance = tokenAmountA.dividedBy(`1e${tokenADecimals || '18'}`).toNumber()
    const tokenBBalance = tokenAmountB.dividedBy(`1e${tokenBDecimals || '18'}`).toNumber()

    const tokenAValue = tokenABalance * (priceList[tokenAInfo?.address]?.usd || (await getPrice(tokenA, chainId)))
    const tokenBValue = tokenBBalance * (priceList[tokenBInfo?.address]?.usd || (await getPrice(tokenB, chainId)))
    let worth = tokenAValue + tokenBValue
    if (tokenAValue === 0) {
      worth = tokenBValue * 2
    } else if (tokenBValue === 0) {
      worth = tokenAValue * 2
    }
    const balance = new BigNumber(lp.balance).dividedBy(`1e${lpDecimals || '18'}`).toFixed()
    const result = {
      ...lp,
      balance,
      tokenA: {
        address: tokenAInfo?.address || tokenA,
        logo: tokenAInfo?.logo,
        name: tokenASymbol,
        amount: tokenAmountA,
        value: tokenABalance,
        decimals: tokenADecimals
      },
      tokenB: {
        address: tokenBInfo?.address || tokenB,
        logo: tokenBInfo?.logo,
        name: tokenBSymbol,
        amount: tokenAmountB,
        value: tokenBBalance,
        decimals: tokenBDecimals
      },
      worth
    }
    return result
  } catch (e) {
    console.log(lp, e)
    return null
  }
}

// Support single token staking
export async function calculateBalanceSingle(
  web3: Web3,
  lp: BaseBalance,
  tokens: Token[],
  priceList?: PriceList
): Promise<Balance> {
  try {
    let token
    const tokenAmount = lp.balance
    const lpInfo = findToken(tokens, lp.lpAddress)
    const contract = new web3.eth.Contract(erc20, lp.lpAddress)
    if (lp.lpAddress === '0xbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb') {
      token = {
        address: lp.lpAddress?.toLowerCase(),
        name: 'BNB',
        logo: lpInfo?.logo,
        amount: new BigNumber(tokenAmount),
        value: +tokenAmount / 1e18,
        decimals: 18
      }
    } else {
      const tokenBalance = new BigNumber(tokenAmount).dividedBy(new BigNumber(`1e${lpInfo?.decimals ?? 18}`)).toNumber()
      token = {
        address: lpInfo?.address || lp.lpAddress,
        name: lpInfo?.symbol || (await contract.methods.symbol().call()),
        logo: lpInfo?.logo,
        amount: new BigNumber(tokenAmount),
        value: tokenBalance,
        decimals: lpInfo?.decimals || (await contract.methods.decimals().call())
      }
    }

    const { worth, usdPerCoin } = await calculateTokenBalance(web3, lp.lpAddress, lp.balance, tokens, priceList)

    const lpUSD = usdPerCoin

    const balance = new BigNumber(lp.balance).dividedBy(`1e${token?.decimals ?? 18}`).toString()

    const result = {
      ...lp,
      balance,
      token,
      worth: isNaN(worth) ? 0 : worth,
      lpUSD
    }
    return result
  } catch (error) {
    console.log(lp, error)
    return null
  }
}

export async function calculateTokenBalance(
  web3: Web3,
  tokenAddress: string,
  balanceString: string,
  tokens: Token[],
  priceList?: PriceList
) {
  try {
    const lpInfo = findToken(tokens, tokenAddress)
    let worth = 0
    let usdPerCoin = priceList[lpInfo?.address]?.usd ?? 0

    if (+balanceString === 0) return { worth: 0, usdPerCoin }

    const _tokenAddress = [mebBNB].indexOf(tokenAddress.toLowerCase()) > -1 ? BNB : tokenAddress.toLowerCase()
    //Balancer
    // if (tokenAddress === BPSP) {
    if (BALANCER_LPs.includes(tokenAddress)) {
      const { calculateBalancerPool } = await import('./platforms/balancer')
      const usdPerCoin = await calculateBalancerPool(web3, tokenAddress, tokens, priceList)
      worth = (+balanceString / 1e18) * usdPerCoin
    } else if (alpacaDebtTokens.includes(tokenAddress.toLowerCase())) {
      const contract = new web3.eth.Contract(debtShareToken as any, tokenAddress)
      const [totalSupply, totalToken, token] = await makeBatchRequest(web3, [
        contract.methods.totalSupply().call,
        contract.methods.totalToken().call,
        contract.methods.token().call
      ])
      const ratio = +totalToken / +totalSupply ?? 1
      usdPerCoin = priceList[token.toLowerCase()]?.usd || 0
      worth = (+balanceString / 1e18) * ratio * usdPerCoin
    } else if ([...IRON_LPs, ...DOP_LPS].includes(_tokenAddress)) {
      //? 3pair IRON,DOPPLE
      const contract = new web3.eth.Contract(erc20, tokenAddress)
      const ownerAddress = await contract.methods.owner().call()

      const swapContract = new web3.eth.Contract(Swap as any, ownerAddress)
      const virtualPrice = await swapContract.methods.getVirtualPrice().call()

      const balance = +balanceString / 1e18
      worth = balance * (+virtualPrice / 1e18)
      usdPerCoin = +virtualPrice / 1e18
    } else if ([BUSD, mebBUSD].indexOf(_tokenAddress) > -1) {
      worth = parseFloat(weiToEth(`${balanceString}`))
    } else if (tokenAddress.toLowerCase() === EPS_3P.toLowerCase()) {
      const value = +balanceString / 1e18
      worth = value
    } else if (tokenAddress.toLowerCase() === WEX.toLowerCase()) {
      const price = priceList[WEX]?.usd ?? 0
      const value = (+balanceString / 1e18) * price
      worth = value
    } else if (priceList[lpInfo?.address]?.usd) {
      let decimals = lpInfo?.decimals
      if (!decimals) {
        const contract = new web3.eth.Contract(erc20, tokenAddress)
        decimals = await contract.methods.decimals().call()
      }
      worth = new BigNumber(balanceString)
        .dividedBy(`1e${decimals || 18}`)
        .multipliedBy(priceList[lpInfo?.address]?.usd || 0)
        .toNumber()
    } else {
      try {
        const contract = new web3.eth.Contract(erc20, tokenAddress)
        const decimals = lpInfo?.decimals || (await contract.methods.decimals().call())
        worth = new BigNumber(balanceString)
          .dividedBy(`1e${decimals || 18}`)
          .multipliedBy(priceList[lpInfo?.address]?.usd || 0)
          .toNumber()
      } catch (e) {
        worth = parseFloat(weiToEth(`${balanceString}`))
      }
    }
    usdPerCoin = isNaN(usdPerCoin) ? 0 : usdPerCoin
    return { worth, usdPerCoin }
  } catch (error) {
    console.log(tokenAddress, error)
    return { worth: 0, usdPerCoin: 0 }
  }
}

export async function splitBalanceLP(web3: Web3, lp: BaseLPBalance): Promise<any> {
  const contractLP = new web3.eth.Contract(pair, lp.lpAddress)
  const [totalSupply, { '0': reserveA, '1': reserveB }, tokenA, tokenB] = await makeBatchRequest(web3, [
    contractLP.methods.totalSupply().call,
    contractLP.methods.getReserves().call,
    contractLP.methods.token0().call,
    contractLP.methods.token1().call
  ])

  const tokenAContract = new web3.eth.Contract(erc20, tokenA?.toLowerCase())
  const tokenBContract = new web3.eth.Contract(erc20, tokenB?.toLowerCase())
  const [tokenASymbol, tokenBSymbol] = await makeBatchRequest(web3, [
    tokenAContract.methods.symbol().call,
    tokenBContract.methods.symbol().call
  ])

  const tokenAmountA = new BigNumber(lp.balance).dividedBy(totalSupply).multipliedBy(reserveA)
  const tokenAmountB = new BigNumber(lp.balance).dividedBy(totalSupply).multipliedBy(reserveB)

  return {
    token0: {
      amount: tokenAmountA,
      amountString: +tokenAmountA.toString() / 1e18,
      value: +tokenAmountA.toString() / 1e18,
      address: tokenA,
      symbol: tokenASymbol,
      reserve: reserveA
    },
    token1: {
      amount: tokenAmountB,
      amountString: +tokenAmountB.toString() / 1e18,
      value: +tokenAmountB.toString() / 1e18,
      address: tokenB,
      symbol: tokenBSymbol,
      reserve: reserveB
    },
    totalSupply
  }
}
